import { apiBase } from '../region'
import { sessionToken } from '../auth'
import { handleRejection } from '../session-state'
import type { Storage } from '../../platform/storage'
import { useKid } from '../../platform/web'
import { KidStorage, currentKid, kids, saveKids, type Kid } from '../../stores/children'
import { mergeDuplicateKids } from '../../stores/kids-boot'
import { rememberTourSeen } from '../../state/store'
import { apply } from './apply'
import { noteSyncOk, noteSyncRejected } from './health'
import { localSnapshot, outgoing, type SyncAnswer } from './payload'
import { markSent, sinceOf } from '../../stores/outbox'
import { clearSelfCheckFindings, selfCheckFindings, selfCheckNotes } from '../../stores/selfcheck'

/**
 * Сам обмен: сходить на сервер и разложить ответ.
 *
 * Без него аккаунт почти ничего не значил: профиль, правки «Моего дня», история окон
 * и заметки жили только в памяти браузера, и обещание «расписание не потеряется при
 * смене телефона» не выполнялось.
 */

async function call(path: string, body?: unknown): Promise<SyncAnswer> {
  const token = sessionToken()
  if (token === null) throw new Error('Нет ключа сессии')
  const response = await fetch(`${apiBase()}${path}`, {
    method: body === undefined ? 'GET' : 'POST',
    headers: { 'content-type': 'application/json', authorization: `Session ${token}` },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  })
  if (!response.ok) {
    // 401 и 410 — не «нет связи», а ответ сервера про аккаунт: он разбирается отдельно
    if (!(await handleRejection(response.status))) noteSyncRejected(response.status)
    throw new Error(`${path} → ${response.status}`)
  }
  noteSyncOk()
  return (await response.json()) as SyncAnswer
}

/** Отправляет список малышей и возвращает ответ сервера. */
async function callMany(
  children: unknown[],
  since: number,
  selfCheck: string[] = [],
  selfCheckWhy: Record<string, string> = {},
): Promise<{ children: SyncAnswer[]; tourSeen?: boolean; serverNow: number } | null> {
  const token = sessionToken()
  if (token === null) return null
  const response = await fetch(`${apiBase()}/sync`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', authorization: `Session ${token}` },
    body: JSON.stringify({
      children,
      since,
      ...(selfCheck.length === 0 ? {} : { selfCheck }),
      // Подробности едут только вместе с находкой и только числами: по ним мы разбираем
      // причину, не дожидаясь, пока человек заметит и расскажет (29.10.2026)
      ...(Object.keys(selfCheckWhy).length === 0 ? {} : { selfCheckWhy }),
    }),
  })
  if (!response.ok) {
    if (!(await handleRejection(response.status))) noteSyncRejected(response.status)
    throw new Error(`/sync → ${response.status}`)
  }
  noteSyncOk()
  return (await response.json()) as { children: SyncAnswer[]; tourSeen?: boolean; serverNow: number }
}

/** Общее хранилище устройства: список малышей лежит там, а не под приставкой. */
function deviceStorageOf(storage: KidStorage): KidStorage {
  return storage
}

/**
 * Обмен, когда малышей может быть несколько: шлём всех, кого знаем, и раскладываем ответ
 * по их хранилищам. Малыш, заведённый на другом устройстве, приезжает в ответе — его
 * данные кладём под новой приставкой и добавляем в список.
 *
 * Список может быть и пустым — так выглядит новый телефон после входа. Тогда посылка
 * пустая, а сервер присылает всех малышей счёта: их и берём, первого показываем.
 */
async function syncKids(storage: KidStorage, onApplied?: (() => void) | null): Promise<boolean> {
  const mine = kids.value
  const payload = []
  /** Что ушло по каждому малышу: после удачного обмена с этого снимаются метки. */
  const корзины: Array<{ kid: string; sent: { events: number[]; windows: number[] }; sentAt: number }> = []
  // Ответ просим с самого раннего «с момента» среди малышей: у нового — с нуля
  let since = Number.POSITIVE_INFINITY
  for (const kid of mine) {
    const своё = storage.forKid(kid.id)
    const out = await outgoing(своё, kid.id)
    if (out !== null) {
      payload.push(out.payload)
      корзины.push({ kid: kid.id, sent: out.sent, sentAt: out.sentAt })
    }
    since = Math.min(since, await sinceOf(своё))
  }
  // Что пришлось починить на устройстве: сервер запишет это в журнал, а сторож скажет нам
  const находки = await selfCheckFindings(deviceStorageOf(storage))
  const почему = находки.length === 0 ? {} : await selfCheckNotes(deviceStorageOf(storage))
  const answer = (await callMany(payload, Number.isFinite(since) ? since : 0, находки, почему)) ?? null
  if (answer === null) return false
  if (находки.length > 0) await clearSelfCheckFindings(deviceStorageOf(storage))
  if (answer.tourSeen === true) await rememberTourSeen()

  let list = mine
  /** Что-то из ответа легло на устройство: экраны надо перечитать. */
  let поменялось = false
  // Сервер постарше отвечает одним малышом, а не списком: принимаем и так
  const приехали = Array.isArray(answer.children)
    ? answer.children
    : ((answer as unknown as SyncAnswer).child ? [answer as unknown as SyncAnswer] : [])
  for (const one of приехали) {
    // Сервер всегда присылает малыша, но по типу ответа он может быть пустым
    if (one.child === null) continue
    // Имя ящика не меняется никогда. Серверный номер, если он другой, записываем рядом:
    // по нему мы узнаем своего малыша, когда он приедет под именем сервера (28.10.2026)
    const id = своёИмя(one, list) ?? one.child.serverId ?? one.child.clientId
    const серверный = one.child.serverId ?? null
    const известен = list.find((k) => k.id === id)
    if (известен === undefined) {
      list = [...list, { id, name: one.child.name, ...(серверный !== null && серверный !== id ? { serverId: серверный } : {}) }]
    } else if (серверный !== null && серверный !== id && известен.serverId !== серверный) {
      list = list.map((k) => (k.id === id ? { ...k, serverId: серверный } : k))
    }
    const своё = storage.forKid(id)
    // Сравниваем снимок до и после: иначе правки, приехавшие с другого устройства
    // (списки ВБ, заметки, записи), лежали бы в хранилище, а экран показывал бы старое —
    // до перезапуска приложения (жалоба заказчика 27.10.2026: «куда пропали пять ВБ»)
    const до = JSON.stringify(await localSnapshot(своё, id))
    await apply(своё, { ...one, serverNow: answer.serverNow })
    if (JSON.stringify(await localSnapshot(своё, id)) !== до) поменялось = true
  }
  /**
   * Текущий малыш обязан быть из списка.
   *
   * На новом телефоне текущего может не быть вовсе: показываем того, с кем работали
   * последним, иначе приложение считает, что малыша нет, и зовёт заполнить анкету заново.
   * А если указатель ведёт мимо списка — так было после неудачного переезда, когда данные
   * лежали под одним именем, а указатель под другим (разбор 28.10.2026), — чиним и это.
   */
  const вСписке = (id: string | null) => id !== null && list.some((k) => k.id === id)
  const current = (вСписке(currentKid.value) ? currentKid.value : null) ?? свежайший(приехали, list) ?? list[0]?.id ?? null
  const появился = current !== null && current !== currentKid.value
  if (list !== mine || появился) {
    await saveKids(deviceStorageOf(storage), list, current)
    if (появился) {
      useKid(current)
      onApplied?.()
    }
  }
  // Та же анкета, заведённая дважды, приезжает сюда двумя малышами: пустую сливаем
  // с той, где история, иначе в списке висят два одинаковых «Паши» (12.10.2026)
  if (поменялось) onApplied?.()
  if ((await mergeDuplicateKids()) !== null) onApplied?.()
  for (const к of корзины) await markSent(storage.forKid(к.kid), к.sent, answer.serverNow, к.sentAt)
  return true
}

/**
 * Под каким именем этот малыш уже лежит на устройстве; null — он здесь новый.
 *
 * Сервер может назвать его любым из имён: тем, под которым мы его прислали, своим
 * серверным или телефонным именем другого устройства. Сверяем со всеми, что знаем, —
 * иначе старый малыш с двумя номерами приехал бы вторым «Пашей».
 */
function своёИмя(one: SyncAnswer, list: Kid[]): string | null {
  if (one.child === null) return null
  const имена = new Set([one.requestedClientId, one.child.serverId ?? undefined, one.child.clientId].filter((x): x is string => typeof x === 'string'))
  for (const k of list) {
    if (имена.has(k.id)) return k.id
    if (k.serverId !== undefined && имена.has(k.serverId)) return k.id
  }
  return null
}

/**
 * Кем из малышей счёта встретить новый телефон: тем, о ком записи свежее. Порядок
 * заведения тут плохой советчик — первым может оказаться старший, которого уже не ведут.
 */
function свежайший(приехали: SyncAnswer[], list: Kid[]): string | null {
  let лучший: { id: string; at: number } | null = null
  for (const one of приехали) {
    if (one.child === null) continue
    const at = Math.max(
      one.child.updatedAtMs,
      ...one.events.map((e) => e.atMs),
      ...one.windows.map((w) => w.startedAtMs),
    )
    // Имя то же, под которым легли данные, — иначе указатель уйдёт в пустую ячейку
    const id = своёИмя(one, list) ?? one.child.serverId ?? one.child.clientId
    if (лучший === null || at > лучший.at) лучший = { id, at }
  }
  return лучший?.id ?? null
}

/**
 * Полный обмен: отдать своё, забрать общее. Возвращает false, если обмен не состоялся —
 * нет входа или не было сети. Это не ошибка: приложение работает и без сервера.
 *
 * `onApplied` зовут, только если сервер действительно что-то изменил. Раньше это была
 * переменная модуля, которую ставил `bindSync`; теперь она приезжает доводом, чтобы
 * очередь отправки и сам обмен не ссылались друг на друга по кругу.
 */
export async function pullKidsOnStart(storage: Storage): Promise<void> {
  if (sessionToken() === null || !(storage instanceof KidStorage) || kids.value.length > 0) return
  try {
    await syncKids(storage)
  } catch {
    // Сети нет — покажем то, что есть на устройстве
  }
}

export async function syncNow(storage: Storage, onApplied?: (() => void) | null): Promise<boolean> {
  if (sessionToken() === null) return false
  try {
    // Пустой список — не повод идти старой дорогой: именно там малыш со счёта ложился
    // в безымянную ячейку и на устройстве так и не заводился (случай 12.10.2026)
    if (storage instanceof KidStorage) return await syncKids(storage, onApplied)
    const before = await localSnapshot(storage)
    const out = await outgoing(storage)
    const answer = out === null ? await call('/sync') : await call('/sync', { ...out.payload, since: await sinceOf(storage) })
    if (answer.tourSeen === true) await rememberTourSeen()
    await apply(storage, answer)
    if (out !== null) await markSent(storage, out.sent, answer.serverNow, out.sentAt)
    // Перечитывать экраны стоит, только если сервер действительно что-то изменил:
    // иначе каждое сохранение дёргало бы интерфейс на ровном месте.
    const after = await localSnapshot(storage)
    if (JSON.stringify(before) !== JSON.stringify(after)) onApplied?.()
    return true
  } catch {
    return false
  }
}
